import { Injectable, inject, DestroyRef } from '@angular/core';
import {
  EMPTY,
  NEVER,
  Subject,
  catchError,
  combineLatest,
  mergeMap,
  of,
  tap
} from 'rxjs';
import { SOCKET_ROOM } from '../../enums/events-buffer/socket/socket-rooms-enum';
import { SOCKET_WAITER_EVENT } from '../../enums/events-buffer/socket/socket-waiters-event.enum';
import { Logger } from '../../interfaces/Logger';
import { ISocketPayload } from '../../interfaces/socket-payload.interface';
import { SocketRoomHandlerService } from '../../services/socket/socket-room-handler.service';
import { SocketService } from '../../services/socket/socket.service';
import { SignatureService } from '../service/signature.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SERVICE_STATE_SERVICE } from '@features/service/domain/tokens/service-state.token';
import { Worker } from '@features/service/domain/models/worker.model';

@Injectable({
  providedIn: 'root',
})
export class WaiterSocketHandlerService {
  private readonly socketService = inject(SocketService);
  private readonly socketRoomHandlerService = inject(SocketRoomHandlerService);
  private readonly signatureService = inject(SignatureService);
  private readonly logger = inject(Logger);
  private readonly destroyRef = inject(DestroyRef);

  private readonly serviceStateService = inject(SERVICE_STATE_SERVICE);

  private destroyed$: Subject<void> = new Subject<void>();

  constructor() {
    this.checkWaitersRoom();
  }

  clearSubscriptions(): void {
    this.destroyed$.next();
  }

  private checkWaitersRoom(): void {
    this.socketRoomHandlerService
      .listenToRoom(SOCKET_ROOM.waiters)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.logger.debug(
            `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.waiters} 👤] [Room joined, clearing previous subscriptions before reinitializing]`
          );
          this.clearSubscriptions();
        })
      )
      .subscribe(() => {
        this.initializeWaiterListeners();
      });
  }

  private initializeWaiterListeners(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.waiters} 🙎‍♂️🙍‍♀️] [Waiters listeners initialized]`
    );
    this.onWaiterAddedListener();
    this.onWaiterRemovedListener();
  }

  private onWaiterAddedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.waiters} 🙎‍♂️🙍‍♀️] [Waiters added listener initialized]`
    );

    this.socketService
      .receive<ISocketPayload>(SOCKET_WAITER_EVENT.added)
      .pipe(
        catchError((error) => {
          this.logger.error(
            new Error('Error receiving waiter added event:'),
            error
          );

          return EMPTY;
        }),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest([
            of(eventResponse),
            this.signatureService.getSignatureFromIndexedDB(),
          ])
        ),
        mergeMap(
          ([eventResponse, currentSignature]: [ISocketPayload, string]) => {
            const waiters = eventResponse.payload.waiters;
            const signature = eventResponse.payload?.signature?.checksum;

            if (signature && signature === currentSignature) {
              return NEVER;
            }

            const signature$ =
              signature && signature !== currentSignature
                ? this.signatureService.insertSignatureOnDB(signature)
                : EMPTY;

            const insertWaiters$ = of(this.serviceStateService.insertWorkers(waiters));

            return combineLatest([insertWaiters$, signature$]);
          }
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.logger.debug(
            `[Socket 🔌] [Room: ${SOCKET_ROOM.waiters} 🙎‍♂️🙍‍♀️] [Waiters added to service]`
          );
        },
        error: (error) => {
          this.logger.error(
            new Error('Error processing waiter addition:'),
            error
          );
        },
      });
  }

  private onWaiterRemovedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.waiters} 🙎‍♂️🙍‍♀️] [Waiters removed listener initialized]`
    );

    this.socketService
      .receive<ISocketPayload>(SOCKET_WAITER_EVENT.removed)
      .pipe(
        catchError((error) => {
          this.logger.error(
            new Error('Error receiving waiter removed event:'),
            error
          );

          return EMPTY;
        }),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest([
            of(eventResponse),
            this.signatureService.getSignatureFromIndexedDB(),
          ])
        ),
        mergeMap(
          ([eventResponse, currentSignature]: [ISocketPayload, string]) =>
            combineLatest([
              of(eventResponse),
              of(currentSignature),
              of(this.serviceStateService.getCurrentWorker()),
            ])
        ),
        mergeMap(
          ([eventResponse, currentSignature, /*currentWorker*/]: [
            ISocketPayload,
            string,
            Worker | null
          ]) => {
            // TODO: Implement this carlos.florio check if we need to remove workers
            //const workersIds = eventResponse.payload.waiters_ids as string[];
            const signature = eventResponse.payload?.signature?.checksum;

            if (signature && signature === currentSignature) {
              return NEVER;
            }

            const signature$ =
              signature && signature !== currentSignature
                ? this.signatureService.insertSignatureOnDB(signature)
                : of('');
                

            // TODO: Implement this carlos.florio check if we need to remove workers
            /*const removeWaiters$ = this.waiterService.removeWaiters(waitersIds);

            if (currentWorker && waitersIds.includes(currentWaiter._id)) {
              currentWaiter.activated = false;

              return combineLatest([
                this.waiterService.insertCurrentWaiter(currentWaiter),
                removeWaiters$,
                signature$,
              ]).pipe(
                tap(() => this.waiterService.notifyOnCurrentWaiterRemoved())
              );
            }

            return combineLatest([removeWaiters$, signature$]);*/

            return signature$;
          }
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.logger.debug(
            `[Socket 🔌] [Room: ${SOCKET_ROOM.waiters} 🙎‍♂️🙍‍♀️] [Waiters removed from service]`
          );
          // TODO: Implement this carlos.florio check if we need to notify on workers removed
          //this.waiterService.notifyWaitersRemoved();
        },
        error: (error) => {
          this.logger.error(
            new Error('Error processing waiter removal:'),
            error
          );
        },
      });
  }
}
